import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  CommandPalette,
  type CommandPaletteItem,
  ConfirmDialog,
  useHotkey,
} from '@rammetec/design-system'
import {
  useAvailableInstances,
  useAvailableModules,
  useSessionManager,
  useUserOrganizations,
  useUserProfiles,
} from '@syscolect/application'
import type { AppModule } from '@/app/modules/registry'
import { focusModuleGridContainer } from '@/shared/focus/focusModuleGrid'

/** The four global-shortcut overlays; only one may be open at a time. */
type Overlay = 'workspace' | 'moduleSearch' | 'orgSwitcher' | 'profileSwitcher'

export function AppShortcuts() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { currentOrganizationId, currentProfileId, switchOrganization, switchProfile } =
    useSessionManager()
  const { userOrganizations } = useUserOrganizations()
  const { profiles: userProfiles } = useUserProfiles()
  const instances = useAvailableInstances()
  const modules = useAvailableModules<AppModule>()

  const isModuleRoute = location.pathname.startsWith('/m/')

  // Single active overlay — the four global shortcuts are mutually exclusive, so
  // opening one replaces whatever was open instead of stacking on top of it.
  const [overlay, setOverlay] = useState<Overlay | null>(null)

  // Deferred module navigation — palette closes before navigate so ConfigurableGrid
  // autoFocus is not blocked by the palette input (DS-0040).
  const pendingModuleHrefRef = useRef<string | null>(null)

  const { moduleItems, moduleHrefs } = useMemo(() => {
    const modBySlug = new Map(modules.map((m) => [m.slug, m]))
    const items: CommandPaletteItem[] = []
    const hrefs = new Map<string, string>()
    for (const inst of instances) {
      const def = modBySlug.get(inst.slug)
      if (!def || def.kind === 'group') continue
      const href = `/m/${inst.slug}?mid=${inst.id}`
      items.push({ id: inst.id, label: inst.label ?? def.label ?? inst.slug, icon: def.iconNode })
      hrefs.set(inst.id, href)
    }
    return { moduleItems: items, moduleHrefs: hrefs }
  }, [instances, modules])

  const orgItems = useMemo<CommandPaletteItem[]>(
    () => userOrganizations.map((org) => ({ id: org.id, label: org.name })),
    [userOrganizations],
  )

  const profileItems = useMemo<CommandPaletteItem[]>(
    () => userProfiles.map((p) => ({ id: p.id, label: p.name })),
    [userProfiles],
  )

  const activeMid = searchParams.get('mid') ?? undefined

  const openWorkspace = useCallback(() => setOverlay('workspace'), [])
  const openModuleSearch = useCallback(() => setOverlay('moduleSearch'), [])
  const openOrgSwitcher = useCallback(() => setOverlay('orgSwitcher'), [])
  const openProfileSwitcher = useCallback(() => setOverlay('profileSwitcher'), [])

  const restoreModuleGridFocus = useCallback((e: Event) => {
    e.preventDefault()
    focusModuleGridContainer()
  }, [])

  const moduleSearchCloseAutoFocus = useCallback(
    (e: Event) => {
      e.preventDefault()
      const href = pendingModuleHrefRef.current
      if (href) {
        pendingModuleHrefRef.current = null
        navigate(href)
        return
      }
      if (isModuleRoute) focusModuleGridContainer()
    },
    [navigate, isModuleRoute],
  )

  // Controlled-overlay props. The `cur === which` guard prevents a dialog that is
  // closing because another overlay just opened from clobbering the new one.
  const overlayProps = useCallback(
    (which: Overlay) => ({
      open: overlay === which,
      onOpenChange: (open: boolean) => {
        if (!open) setOverlay((cur) => (cur === which ? null : cur))
      },
    }),
    [overlay],
  )

  useHotkey('Ctrl+Alt+D', openWorkspace)
  useHotkey('Ctrl+Alt+S', openModuleSearch)

  // Ctrl+Alt+E and Ctrl+Alt+W can't use useHotkey because on Windows the
  // Ctrl+Alt combination is equivalent to AltGr, which on Brazilian ABNT
  // keyboards maps KeyE → '€' and KeyW → '?' — so event.key never matches
  // 'e'/'w'. We bind by event.code (physical key) instead.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.ctrlKey || !e.altKey) return
      if (e.code === 'KeyE') { e.preventDefault(); openOrgSwitcher() }
      if (e.code === 'KeyW') { e.preventDefault(); openProfileSwitcher() }
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [openOrgSwitcher, openProfileSwitcher])

  return (
    <>
      <ConfirmDialog
        {...overlayProps('workspace')}
        title="Ir para a área de trabalho?"
        description="O trabalho não salvo será perdido."
        confirmLabel="Ir"
        variant="warning"
        autoFocusConfirm
        onConfirm={() => {
          setOverlay(null)
          navigate('/')
        }}
      />

      <CommandPalette
        {...overlayProps('moduleSearch')}
        items={moduleItems}
        onSelect={(id) => {
          const href = moduleHrefs.get(id)
          if (href) pendingModuleHrefRef.current = href
        }}
        onCloseAutoFocus={moduleSearchCloseAutoFocus}
        activeId={activeMid}
        searchPlaceholder="Buscar módulo…"
        warning="O trabalho não salvo será perdido."
      />

      <CommandPalette
        {...overlayProps('orgSwitcher')}
        items={orgItems}
        onSelect={(orgId) => {
          void switchOrganization(orgId).then(() => navigate('/'))
        }}
        onCloseAutoFocus={isModuleRoute ? restoreModuleGridFocus : undefined}
        activeId={currentOrganizationId ?? undefined}
        searchPlaceholder="Buscar organização…"
        warning="Você será redirecionado para a área de trabalho."
      />

      <CommandPalette
        {...overlayProps('profileSwitcher')}
        items={profileItems}
        onSelect={(profileId) => {
          void switchProfile(profileId).then(() => navigate('/'))
        }}
        onCloseAutoFocus={isModuleRoute ? restoreModuleGridFocus : undefined}
        activeId={currentProfileId ?? undefined}
        searchPlaceholder="Buscar perfil…"
        warning="Você será redirecionado para a área de trabalho."
      />
    </>
  )
}
